
import React, { useEffect } from 'react';

interface SEOManagerProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  faq?: { question: string; answer: string }[];
  price?: string;
  currency?: string;
  settings?: any;
  breadcrumbs?: { name: string; item: string }[];
}

const SEOManager: React.FC<SEOManagerProps> = ({
  title,
  description,
  url,
  image,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  section,
  faq,
  price,
  currency,
  settings,
  breadcrumbs
}) => {
  useEffect(() => {
    document.title = title;

    const updateMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) meta.setAttribute('property', name);
        else meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMeta('description', description);
    updateMeta('og:type', type, true);
    updateMeta('og:url', url, true);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    if (image) updateMeta('og:image', image, true);

    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    if (image) updateMeta('twitter:image', image);
    
    // Google Site Verification
    if (settings?.googleSiteVerification) {
      // Extract content if the user pasted the whole tag
      let content = settings.googleSiteVerification;
      if (content.includes('content="')) {
        const match = content.match(/content="([^"]+)"/);
        if (match) content = match[1];
      } else if (content.includes("content='")) {
        const match = content.match(/content='([^']+)'/);
        if (match) content = match[1];
      }
      updateMeta('google-site-verification', content);
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

  }, [title, description, url, image, type]);

  // Schema generation (keep as is, but maybe inject it into head as well)
  const siteName = settings?.siteTitle || "Portal de Conteúdo";
  const logoUrl = settings?.logoUrl || "";
  
  let origin = "";
  try {
    origin = new URL(url).origin;
  } catch (e) {
    if (typeof window !== 'undefined') {
      origin = window.location.origin;
    }
  }

  const socialLinks = [
    settings?.instagramUrl,
    settings?.facebookUrl,
    settings?.linkedInUrl,
    settings?.youtubeUrl,
    settings?.tiktokUrl,
    settings?.xUrl,
    ...(settings?.structuredDataOtherSocials?.split('\n').filter((s: string) => s.trim() !== '') || [])
  ].filter(Boolean);

  const entityType = settings?.structuredDataType || "Organization";
  const entityName = settings?.structuredDataName || siteName;
  const entityUrl = settings?.structuredDataUrl || origin;

  const graph: any[] = [
    {
      "@type": entityType,
      "@id": `${origin}/#entity`,
      "name": entityName,
      "url": entityUrl,
      ...(entityType === 'Organization' 
        ? { "logo": { "@type": "ImageObject", "url": settings?.structuredDataLogo || logoUrl } }
        : { "image": { "@type": "ImageObject", "url": settings?.structuredDataImage || image } }
      ),
      "sameAs": socialLinks
    },
    {
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      "url": origin,
      "name": siteName,
      "publisher": {
        "@id": `${origin}/#entity`
      }
    }
  ];

  if (type === 'article') {
    graph.push({
      "@type": "BlogPosting",
      "@id": `${url}/#article`,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      },
      "headline": title,
      "description": description,
      "image": image ? [image] : [],
      "datePublished": publishedTime,
      "dateModified": modifiedTime || publishedTime,
      "author": {
        "@type": "Person",
        "name": author || siteName,
        "url": settings?.structuredDataUrl || origin
      },
      "publisher": {
        "@id": `${origin}/#entity`
      },
      "articleSection": section || "Tecnologia",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".article-title", ".article-excerpt", ".key-takeaways"]
      }
    });
  }

  if (type === 'product') {
    graph.push({
      "@type": "Product",
      "@id": `${url}/#product`,
      "name": title,
      "image": image ? [image] : [],
      "description": description,
      "brand": {
        "@type": "Brand",
        "name": siteName
      },
      "offers": {
        "@type": "Offer",
        "url": url,
        "priceCurrency": currency || "BRL",
        "price": price || "0.00",
        "availability": "https://schema.org/InStock"
      }
    });
  }

  if (faq && faq.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}/#faq`,
      "mainEntity": faq.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${url}/#breadcrumb`,
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.item.startsWith('http') ? crumb.item : `${origin}${crumb.item}`
      }))
    });
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": graph
  };

  useEffect(() => {
    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }, [JSON.stringify(schema)]);

  return null;
};

export default SEOManager;
